import { ListFilter, MousePointerClick, SearchX, Settings2, TriangleAlert } from "lucide-react";
import { type Transition, motion, useReducedMotion } from "motion/react";
import { type KeyboardEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useMatchingUnreadCount } from "../api/useMatchingUnreadCount";
import { useNotificationActionMutation } from "../api/useNotificationActionMutation";
import { useNotificationDetail } from "../api/useNotificationDetail";
import { useNotificationFilters } from "../api/useNotificationFilters";
import { useNotificationsPage } from "../api/useNotificationsPage";
import { NotificationDetail } from "../components/NotificationDetail";
import { NotificationFeed } from "../components/NotificationFeed";
import type { NotificationRowProps } from "../components/NotificationRow";
import { NotificationRowSkeleton } from "../components/NotificationRowSkeleton";
import { NotificationSelectionBar } from "../components/NotificationSelectionBar";
import { NotificationStatusTabs } from "../components/NotificationStatusTabs";
import { NOTIFICATION_EMPTY_STATES, focusNeighbourRow, translateFilterOptions } from "../components/utils";
import {
  NOTIFICATION_DETAIL_PARAM,
  NOTIFICATION_FILTER_PARAMS,
  READ_TOGGLE_ACTIONS,
  type NotificationAction,
} from "../constants";
import { useDateRangePickerKey } from "../hooks/useDateRangePickerKey";
import { useNotificationPageFilters } from "../hooks/useNotificationPageFilters";
import { useNotificationSelection } from "../hooks/useNotificationSelection";
import { notificationStatusSchema } from "../schemas";
import type {
  NotificationData,
  NotificationDetailActions,
  NotificationFilterOptions,
  NotificationFilters,
  NotificationStatus,
} from "../types";
import {
  doesActionLeaveView,
  flattenNotificationPages,
  getArchiveToggleAction,
  getBulkArchiveAction,
  getBulkReadAction,
  getReadToggleAction,
} from "../utils";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import { DateRangePicker, type DateRangePickerProps } from "@/components/ui/v2/date-range-picker";
import EmptyState from "@/components/ui/v2/empty-state";
import { FiltersDrawer, type FilterSectionConfig } from "@/components/ui/v2/filters/FiltersDrawer";
import { TableActiveFilters, type ActiveFilter } from "@/components/ui/v2/table/table-active-filters";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";
import { URL_PARAMS } from "@/constants/urlParams";
import { cn } from "@/lib/utils/cn";
import { clearFilterValues, removeFilterValue } from "@/lib/utils/filterParams";
import { formatLocalDateOnly } from "@/utils/dateUtils";

const SCROLL_TARGET_ID = "notifications-page-scroll";
const SKELETON_ROWS = [0, 1, 2, 3, 4];

const FILTER_SECTIONS = [
  {
    key: NOTIFICATION_FILTER_PARAMS.SEVERITY,
    title: "notifications.filters.severity",
    optionsKey: "severity",
    filtersKey: "severities",
  },
  {
    key: NOTIFICATION_FILTER_PARAMS.ACTOR_ID,
    title: "notifications.filters.actor",
    optionsKey: "actor",
    filtersKey: "actorIds",
  },
  {
    key: NOTIFICATION_FILTER_PARAMS.CLIENT_ID,
    title: "notifications.filters.client",
    optionsKey: "client",
    filtersKey: "clientIds",
  },
] as const satisfies ReadonlyArray<{
  key: string;
  title: string;
  optionsKey: keyof NotificationFilterOptions;
  filtersKey: keyof Pick<NotificationFilters, "severities" | "actorIds" | "clientIds">;
}>;

const FILTER_KEYS = FILTER_SECTIONS.map((section) => section.key);

const RESET_KEYS = [...FILTER_KEYS, URL_PARAMS.SEARCH, URL_PARAMS.DATE_FROM, URL_PARAMS.DATE_TO];

const detailTransition: Transition = { type: "spring", bounce: 0, duration: 0.3 };

const reducedMotionTransition: Transition = { duration: 0.15 };

type DateRangeUpdate = Parameters<NonNullable<DateRangePickerProps["onUpdate"]>>[0];

type NotificationsListProps = Pick<
  React.ComponentProps<typeof NotificationFeed>,
  "onAction" | "selection" | "selectedId"
> & {
  viewStatus: NotificationStatus;
  query: ReturnType<typeof useNotificationsPage>;
  notifications: NotificationData[];
  isFiltered: boolean;
  onResetFilters: () => void;
  getRowHref: (notification: NotificationData) => string;
};

function NotificationsList({
  query,
  notifications,
  isFiltered,
  onResetFilters,
  viewStatus,
  onAction,
  selection,
  selectedId,
  getRowHref,
}: NotificationsListProps) {
  const { t } = useTranslation();

  if (query.isLoading) {
    return (
      <div className="divide-y" aria-busy="true">
        {SKELETON_ROWS.map((row) => (
          <NotificationRowSkeleton key={row} />
        ))}
      </div>
    );
  }

  if (query.isError && notifications.length === 0) {
    return (
      <EmptyState
        icon={TriangleAlert}
        title={t("notifications.page.errorTitle")}
        description={t("notifications.page.errorDescription")}
        action={{ label: t("common.retry"), onClick: () => void query.refetch() }}
      />
    );
  }

  if (notifications.length === 0 && isFiltered) {
    return (
      <EmptyState
        icon={SearchX}
        title={t("notifications.page.filteredEmptyTitle")}
        description={t("notifications.page.filteredEmptyDescription")}
        action={{ label: t("buttons.resetFilters"), onClick: onResetFilters, icon: <ListFilter /> }}
      />
    );
  }

  if (notifications.length === 0) {
    const emptyState = NOTIFICATION_EMPTY_STATES[viewStatus];
    return (
      <EmptyState
        icon={emptyState.icon}
        title={t(emptyState.title)}
        description={t(emptyState.description)}
      />
    );
  }

  return (
    <NotificationFeed
      surface="page"
      className="min-h-0 flex-1"
      scrollTargetId={SCROLL_TARGET_ID}
      notifications={notifications}
      fetchNextPage={query.fetchNextPage}
      hasNextPage={query.hasNextPage}
      isFetchingNextPage={query.isFetchingNextPage}
      onAction={onAction}
      selection={selection}
      selectedId={selectedId}
      getRowHref={getRowHref}
    />
  );
}

type NotificationDetailPaneProps = {
  selectedId?: string;
  notification?: NotificationData;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  closeHref: string;
  onAction: NotificationDetailActions["onAction"];
};

function NotificationDetailPane({
  selectedId,
  notification,
  isLoading,
  isError,
  onRetry,
  closeHref,
  onAction,
}: NotificationDetailPaneProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();

  if (!selectedId) {
    return (
      <div data-slot="pane" className="flex h-full items-center justify-center rounded-lg border border-dashed">
        <EmptyState
          icon={MousePointerClick}
          title={t("notifications.detail.selectTitle")}
          description={t("notifications.detail.selectDescription")}
        />
      </div>
    );
  }

  if (!notification && isLoading) {
    return (
      <div data-slot="pane" className="h-full rounded-lg border" aria-busy="true">
        <NotificationRowSkeleton />
      </div>
    );
  }

  if (!notification && isError) {
    return (
      <div data-slot="pane" className="flex h-full items-center justify-center rounded-lg border">
        <EmptyState
          icon={TriangleAlert}
          title={t("notifications.page.errorTitle")}
          description={t("notifications.page.errorDescription")}
          action={{ label: t("common.retry"), onClick: onRetry }}
        />
      </div>
    );
  }

  if (!notification) {
    return (
      <div data-slot="pane" className="flex h-full items-center justify-center rounded-lg border">
        <EmptyState
          icon={SearchX}
          title={t("notifications.detail.notFoundTitle")}
          description={t("notifications.detail.notFoundDescription")}
          action={{ label: t("notifications.detail.close"), href: closeHref }}
        />
      </div>
    );
  }

  const actions: NotificationDetailActions = {
    readAction: getReadToggleAction(notification),
    archiveAction: getArchiveToggleAction(notification),
    onAction,
  };

  return (
    <motion.div
      className="h-full"
      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={prefersReducedMotion ? reducedMotionTransition : detailTransition}
    >
      <NotificationDetail notification={notification} closeHref={closeHref} actions={actions} />
    </motion.div>
  );
}

export default function NotificationsPage() {
  const { t } = useTranslation();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { filters, searchValue, setSearchValue, searchParams, setSearchParams } =
    useNotificationPageFilters();

  const selectedId = searchParams.get(NOTIFICATION_DETAIL_PARAM) ?? undefined;

  const filterOptionsQuery = useNotificationFilters({
    status: filters.status,
    search: filters.search,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });
  const notificationsQuery = useNotificationsPage(filters);
  const notificationAction = useNotificationActionMutation();
  const { pickerKey: dateRangePickerKey, markApplied: markDateRangeApplied } = useDateRangePickerKey(filters);

  const notifications = useMemo(
    () => flattenNotificationPages(notificationsQuery.data?.pages),
    [notificationsQuery.data],
  );
  const totalRecords = notificationsQuery.data?.pages[0]?.totalRecords ?? 0;
  const selection = useNotificationSelection({ filters, notifications, totalRecords });
  const matchingTarget =
    selection.target && "filter" in selection.target && filters.status === notificationStatusSchema.enum.inbox
      ? { filter: selection.target.filter, createdAtOrBefore: selection.target.createdAtOrBefore }
      : null;
  const matchingUnreadCountQuery = useMatchingUnreadCount(matchingTarget);
  const bulkReadAction = getBulkReadAction({
    viewStatus: filters.status,
    mode: selection.mode,
    hasUnreadSelected: selection.hasUnreadSelected,
    matchingUnreadCount:
      matchingUnreadCountQuery.data === undefined
        ? undefined
        : matchingUnreadCountQuery.data - selection.excludedUnreadCount,
  });
  const filterOptions = useMemo(
    () => filterOptionsQuery.data && translateFilterOptions(filterOptionsQuery.data, t),
    [filterOptionsQuery.data, t],
  );

  const filterSections: FilterSectionConfig[] = FILTER_SECTIONS.map((section) => ({
    key: section.key,
    title: section.title,
    options: filterOptions?.[section.optionsKey] ?? [],
  }));

  const activeFilters: ActiveFilter[] = FILTER_SECTIONS.flatMap((section) =>
    filters[section.filtersKey].map((value) => ({
      key: section.key,
      label: t(section.title),
      value,
      valueLabel:
        filterOptions?.[section.optionsKey].find((option) => option.value === value)?.label ?? value,
    })),
  );

  const isFiltered =
    activeFilters.length > 0 ||
    Boolean(filters.search) ||
    searchParams.has(URL_PARAMS.DATE_FROM) ||
    searchParams.has(URL_PARAMS.DATE_TO);

  const loadedSelectedNotification = notifications.find((notification) => notification.id === selectedId);
  const [lastShownNotification, setLastShownNotification] = useState<NotificationData>();
  if (loadedSelectedNotification && loadedSelectedNotification !== lastShownNotification) {
    setLastShownNotification(loadedSelectedNotification);
  }
  const detailQuery = useNotificationDetail(
    selectedId && !loadedSelectedNotification && !notificationsQuery.isLoading ? selectedId : null,
    lastShownNotification?.id === selectedId ? lastShownNotification : undefined,
  );
  const selectedNotification = loadedSelectedNotification ?? detailQuery.data ?? undefined;

  const withDetailParam = (notificationId?: string) => {
    const next = new URLSearchParams(searchParams);
    if (notificationId) next.set(NOTIFICATION_DETAIL_PARAM, notificationId);
    else next.delete(NOTIFICATION_DETAIL_PARAM);
    return `?${next.toString()}`;
  };

  const updateDetailForAction = (action: NotificationAction, isSelectedTargeted: boolean) => {
    if (!isSelectedTargeted || !doesActionLeaveView(filters.status, action, false)) return;
    const next = new URLSearchParams(searchParams);
    next.delete(NOTIFICATION_DETAIL_PARAM);
    setSearchParams(next);
  };

  const handleRowAction: NotificationRowProps["onAction"] = (notificationId, action, options) => {
    updateDetailForAction(action, notificationId === selectedId && !options?.keepInView);
    notificationAction.mutate({
      action,
      target: { notificationIds: [notificationId] },
      keepInView: options?.keepInView ? filters : undefined,
    });
  };

  const handleDetailAction = (action: NotificationAction) => {
    if (!selectedId) return;
    const keepInView = READ_TOGGLE_ACTIONS.has(action);
    const feed = document.getElementById(SCROLL_TARGET_ID);
    if (feed && doesActionLeaveView(filters.status, action, keepInView)) {
      requestAnimationFrame(() => focusNeighbourRow(feed, selectedId));
    }
    handleRowAction(selectedId, action, { keepInView });
  };

  const handleBulkAction = (action: NotificationAction) => {
    const { target } = selection;
    if (!target) return;
    updateDetailForAction(action, Boolean(selectedNotification && selection.isChecked(selectedNotification)));
    notificationAction.mutate({ action, target }, { onSuccess: selection.clear });
  };

  const handleListKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || selection.mode === "none") return;
    event.preventDefault();
    selection.clear();
  };

  const handleStatusChange = (status: NotificationStatus) => {
    const next = new URLSearchParams(searchParams);
    if (status === notificationStatusSchema.enum.unread) next.delete(NOTIFICATION_FILTER_PARAMS.STATUS);
    else next.set(NOTIFICATION_FILTER_PARAMS.STATUS, status);
    setSearchParams(next);
  };

  const handleRemoveFilter = (filter: ActiveFilter) => {
    setSearchParams(removeFilterValue(searchParams, filter.key, String(filter.value)));
  };

  const handleClearFilters = () => {
    setSearchParams(clearFilterValues(searchParams, FILTER_KEYS));
  };

  const handleResetFilters = () => {
    setSearchValue("");
    setSearchParams(clearFilterValues(searchParams, RESET_KEYS));
  };

  const handleDateRangeChange = ({ range }: DateRangeUpdate) => {
    if (!range.from) return;
    const dateFrom = formatLocalDateOnly(range.from);
    const dateTo = formatLocalDateOnly(range.to ?? range.from);
    const next = new URLSearchParams(searchParams);
    next.set(URL_PARAMS.DATE_FROM, dateFrom);
    next.set(URL_PARAMS.DATE_TO, dateTo);
    markDateRangeApplied({ dateFrom, dateTo });
    setSearchParams(next);
  };

  return (
    <ModernPageLayout
      title={t("notifications.title")}
      subtitle={t("notifications.subtitle")}
      actions={
        <Button variant="outline" asChild>
          <Link to={`/${ROUTES.notifications}/${ROUTES.settings}`}>
            <Settings2 />
            {t("notificationSettings.openSettings")}
          </Link>
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <NotificationStatusTabs
          statuses={notificationStatusSchema.options}
          status={filters.status}
          onChange={handleStatusChange}
        />

        <TableToolbar
          searchValue={searchValue ?? ""}
          onSearchChange={setSearchValue}
          onFiltersClick={() => setIsFiltersOpen(true)}
          actions={
            <DateRangePicker
              key={dateRangePickerKey}
              initialDateFrom={filters.dateFrom}
              initialDateTo={filters.dateTo}
              onUpdate={handleDateRangeChange}
              align="end"
            />
          }
        />

        <TableActiveFilters
          filters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearFilters={handleClearFilters}
        />

        <div className="grid h-[70dvh] min-h-96 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
          <section
            aria-label={t("notifications.title")}
            onKeyDown={handleListKeyDown}
            data-slot="pane"
            className={cn(
              "flex min-h-0 flex-col overflow-hidden rounded-lg border",
              selectedId && "hidden lg:flex",
            )}
          >
            {notifications.length > 0 && (
              <NotificationSelectionBar
                selection={selection}
                totalRecords={totalRecords}
                readAction={bulkReadAction}
                isReadActionLoading={matchingTarget !== null && matchingUnreadCountQuery.isLoading}
                archiveAction={getBulkArchiveAction(filters.status)}
                isPending={notificationAction.isPending}
                onAction={handleBulkAction}
              />
            )}
            <NotificationsList
              query={notificationsQuery}
              notifications={notifications}
              isFiltered={isFiltered}
              onResetFilters={handleResetFilters}
              viewStatus={filters.status}
              onAction={handleRowAction}
              selection={{ isChecked: selection.isChecked, onToggle: selection.toggle }}
              selectedId={selectedId}
              getRowHref={(notification) => withDetailParam(notification.id)}
            />
          </section>
          <aside
            aria-label={t("notifications.detail.label")}
            className={cn("min-h-0", !selectedId && "hidden lg:block")}
          >
            <NotificationDetailPane
              selectedId={selectedId}
              notification={selectedNotification}
              isLoading={notificationsQuery.isLoading || detailQuery.isLoading}
              isError={detailQuery.isError}
              onRetry={() => void detailQuery.refetch()}
              closeHref={withDetailParam()}
              onAction={handleDetailAction}
            />
          </aside>
        </div>
      </div>

      <FiltersDrawer
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        searchParams={searchParams}
        filterKeys={FILTER_KEYS}
        filterSections={filterSections}
        entityName={t("notifications.title")}
        filtersLoading={filterOptionsQuery.isLoading}
        preserveParamKeys={[
          URL_PARAMS.SEARCH,
          URL_PARAMS.DATE_FROM,
          URL_PARAMS.DATE_TO,
          NOTIFICATION_FILTER_PARAMS.STATUS,
          NOTIFICATION_DETAIL_PARAM,
        ]}
        defaultExpanded
      />
    </ModernPageLayout>
  );
}
